import type { AiEpisode } from './aiReviewTypes';

/**
 * 🔎 AI 2차 검토 표식 — 순수 함수. (2026-10-18 · 사용자 결정 22:2x · BACKLOG I-94 · docs/impl/153)
 *
 * 스윙(6개월) 계좌(SWING_A)의 감시 종목이 1단선에 닿으면 "저점 신호 검토" 기록이 생긴다. 그 특별한 성격을
 * 스윙(6개월) 화면에서 알아보게 표식 하나(🔎)로 통일한다 — 뜻은 "AI 2차 검토" 이고, 추천 · 매수 신호가 아니라 참고 자료다.
 * 🔴 SWING_A 만이다 — 스윙(3개월) SWING_C · 미국 스윙 SWING_US_A 에는 붙이지 않는다(감시가 SWING_A 감시 표만 본다)
 */

/** 표식 — 탭 · 안내 · 칩 · 도움말이 같은 글자를 쓴다 */
export const AI_REVIEW_MARK = '🔎';

/** 화면 읽기 프로그램 · 마우스 올림용 뜻 */
export const AI_REVIEW_MARK_LABEL = 'AI 2차 검토 대상';

/** AI 2차 검토 대상 계좌인가 — 스윙(6개월)만 */
export function isAiReviewAccount(key: string): boolean {
  return key === 'SWING_A';
}

/**
 * 저점 신호 검토 화면 주소 — 국면 id 를 주면 그 국면 카드를 펼친다(AiReviewPanel 이 ep 를 읽는다).
 * 예) aiReviewHref() → /trading?view=review · aiReviewHref(17) → /trading?view=review&ep=17
 */
export function aiReviewHref(episodeId?: number): string {
  return episodeId === undefined ? '/trading?view=review' : `/trading?view=review&ep=${episodeId}`;
}

/**
 * 스윙(6개월) 안내의 [보고서 보기] 가 주소와 함께 싣는 표시 — 추천 화면이 받으면 하위 탭 자리로 내려 보인다.
 * 하위 탭은 검색 · 관심종목 아래라, 그냥 주소만 바꾸면 폰에서는 "추천 탭 맨 위" 만 보였다(2026-10-19 사용자 "그냥 추천 탭으로 이동한다").
 * 하위 탭을 눌러 바꿀 때는 이 표시가 없어 화면이 움직이지 않는다.
 */
export const AI_REVIEW_SCROLL_STATE = { scrollToReview: true } as const;

/** 주소 이동에 실린 state 가 위 표시인가 */
export function wantsReviewScroll(state: unknown): boolean {
  return typeof state === 'object' && state !== null && (state as { scrollToReview?: unknown }).scrollToReview === true;
}

/**
 * 감시 표 칩 — 종목코드 → 열린 국면. 닫힌 국면은 넣지 않는다(칩은 "지금 검토 중" 이라는 뜻이다).
 * 같은 종목에 열린 국면이 여럿이면(서버가 막지만 혹시) 가장 늦게 연 것 하나 · 연 시각이 같으면 id 가 큰 것.
 *
 * 🔴 종목코드는 글자 그대로 견준다 — 감시 표(SwingTarget.symbol)와 국면(AiEpisode.symbol)이 둘 다 종목 마스터의 symbol
 *   ("005930" 처럼 앞자리 0 이 붙은 여섯 자리)이라 숫자로 바꾸면 오히려 앞자리 0 이 빠져 어긋난다.
 */
export function openEpisodesBySymbol(episodes: readonly AiEpisode[]): Map<string, AiEpisode> {
  const bySymbol = new Map<string, AiEpisode>();
  for (const episode of episodes) {
    if (episode.status !== 'OPEN') {
      continue;
    }
    const kept = bySymbol.get(episode.symbol);
    if (kept === undefined || isLater(episode, kept)) {
      bySymbol.set(episode.symbol, episode);
    }
  }
  return bySymbol;
}

/** 연 시각(ISO 문자열 · 같은 꼴이라 글자 비교가 시각 비교다)이 늦은 쪽 · 같으면 id 가 큰 쪽 */
function isLater(a: AiEpisode, b: AiEpisode): boolean {
  if (a.openedAt !== b.openedAt) {
    return a.openedAt > b.openedAt;
  }
  return a.id > b.id;
}

/** 감시 표 칩 글자 — "🔎 3일째"(국면 며칠째 · 연 날 = 1) */
export function aiReviewChipText(episode: AiEpisode): string {
  return `${AI_REVIEW_MARK} ${episode.phaseDay}일째`;
}

/**
 * 주소의 ep 쿼리 → 펼칠 국면 id. 값이 없거나 숫자가 아니거나 목록에 없으면 null(지금과 똑같이 아무것도 펼치지 않는다).
 */
export function focusEpisodeId(param: string | null, episodes: readonly AiEpisode[]): number | null {
  if (param === null || !/^\d+$/.test(param)) {
    return null;
  }
  const id = Number(param);
  return episodes.some((e) => e.id === id) ? id : null;
}
